/**
 * 블랙리스트 규칙 관리 페이지
 *
 * 관리자가 MAC 주소 또는 전화번호를 사전에 등록해두면
 * 해당 기기/번호가 처음 등록을 시도하는 순간 차단된다.
 *
 * 상태 영속화: useSearchParams 기반 URL 쿼리 파라미터
 *   ?q=검색어&sort=createdAt&dir=desc&page=1
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { blacklistRuleService, BlacklistRuleUpdateRequest } from "../../services/blacklistRuleService";
import { BlacklistRule, BlacklistRuleCreateRequest, DEVICE_VERSION_OPTIONS } from "../../types/blacklistRule";
import { useState, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import ErrorAlert from "../../components/ui/ErrorAlert";
import { formatDate } from "../../utils/format";

// ─── 상수 ─────────────────────────────────────────────────────────────────

const REASONS = ["A", "B", "C", "D", "E", "F", "G"];

const REASON_META: Record<string, { label: string; bg: string; text: string; border: string }> = {
  A: { label: "A - 사기/악용",   bg: "bg-red-50",     text: "text-red-600",     border: "border-red-200" },
  B: { label: "B - 분실/도난",   bg: "bg-orange-50",  text: "text-orange-600",  border: "border-orange-200" },
  C: { label: "C - 불량 기기",   bg: "bg-yellow-50",  text: "text-yellow-600",  border: "border-yellow-200" },
  D: { label: "D - 계정 도용",   bg: "bg-purple-50",  text: "text-purple-600",  border: "border-purple-200" },
  E: { label: "E - 어뷰징",      bg: "bg-blue-50",    text: "text-blue-600",    border: "border-blue-200" },
  F: { label: "F - 테스트 차단", bg: "bg-green-50",   text: "text-green-600",   border: "border-green-200" },
  G: { label: "G - 기타",        bg: "bg-gray-100",   text: "text-gray-600",    border: "border-gray-200" },
};

const ITEMS_PER_PAGE = 50;

type SortKey = "createdAt" | "reason" | "deviceVersion";
type SortDir = "asc" | "desc";

// ─── 서브 컴포넌트 ─────────────────────────────────────────────────────────

function SortIcon({ col, sortKey, sortDir }: { col: SortKey; sortKey: SortKey; sortDir: SortDir }) {
  if (col !== sortKey) return <i className="fas fa-sort text-gray-400 ml-1" />;
  return sortDir === "asc"
    ? <i className="fas fa-arrow-up text-primary ml-1" />
    : <i className="fas fa-arrow-down text-primary ml-1" />;
}

function VersionBadge({ version }: { version: string | null }) {
  if (!version) return <span className="text-gray-400 text-xs">—</span>;
  const cls = version === "V1"
    ? "bg-blue-50 text-blue-600 border-blue-200"
    : "bg-purple-50 text-purple-600 border-purple-200";
  return (
    <span className={`px-3 py-1 inline-flex items-center text-xs leading-5 font-semibold rounded-full border ${cls}`}>
      <i className="fas fa-mobile-alt mr-1"></i>{version}
    </span>
  );
}

function ReasonBadge({ reason }: { reason: string }) {
  const m = REASON_META[reason] ?? REASON_META.G;
  return (
    <span className={`px-3 py-1 inline-flex items-center text-xs leading-5 font-semibold rounded-full border ${m.bg} ${m.text} ${m.border}`}>
      사유 {reason}
    </span>
  );
}

// ─── 수정 모달 ─────────────────────────────────────────────────────────────

function EditModal({
  rule,
  onConfirm,
  onCancel,
  isPending,
}: {
  rule: BlacklistRule;
  onConfirm: (req: BlacklistRuleUpdateRequest) => void;
  onCancel: () => void;
  isPending: boolean;
}) {
  const [reason, setReason] = useState(rule.reason);
  const [note, setNote] = useState(rule.note ?? "");
  const [deviceVersion, setDeviceVersion] = useState(rule.deviceVersion ?? "");

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 w-[480px] shadow-2xl mx-4">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
            <i className="fas fa-edit text-blue-500"></i>
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">블랙리스트 규칙 수정</h3>
            <p className="text-xs text-gray-400">MAC/전화번호는 변경할 수 없습니다.</p>
          </div>
        </div>

        {/* 읽기 전용 정보 */}
        <div className="bg-gray-50 rounded-xl p-4 mb-5 space-y-2 text-sm">
          {rule.deviceAddress && (
            <div className="flex items-center gap-2">
              <span className="text-gray-400 w-20 shrink-0">기기 주소</span>
              <code className="text-xs bg-white px-2 py-1 rounded border border-gray-200 text-gray-700">{rule.deviceAddress}</code>
            </div>
          )}
          {rule.phoneNumber && (
            <div className="flex items-center gap-2">
              <span className="text-gray-400 w-20 shrink-0">전화번호</span>
              <span className="text-gray-700"><i className="fas fa-phone text-gray-400 mr-1"></i>{rule.phoneNumber}</span>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">사유 코드</label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              {REASONS.map((r) => (
                <option key={r} value={r}>{REASON_META[r]?.label ?? `사유 ${r}`}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">기기 버전</label>
            <select
              value={deviceVersion}
              onChange={(e) => setDeviceVersion(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              {DEVICE_VERSION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">메모</label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="관리자 메모"
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={onCancel}
            disabled={isPending}
            className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
          >
            취소
          </button>
          <button
            onClick={() => onConfirm({ reason, note: note.trim() || undefined, deviceVersion: deviceVersion || undefined })}
            disabled={isPending}
            className="flex-1 py-2.5 bg-primary hover:bg-[#5FA060] rounded-xl text-sm font-medium text-white disabled:opacity-50 transition-colors"
          >
            {isPending ? "저장 중..." : "저장"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── 삭제 모달 ─────────────────────────────────────────────────────────────

function ConfirmDeleteModal({
  rule,
  onConfirm,
  onCancel,
  isPending,
}: {
  rule: BlacklistRule;
  onConfirm: () => void;
  onCancel: () => void;
  isPending: boolean;
}) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 w-80 shadow-2xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
            <i className="fas fa-ban text-text-red"></i>
          </div>
          <h3 className="text-lg font-bold text-gray-900">규칙 삭제</h3>
        </div>
        <p className="text-sm text-gray-600 mb-4">아래 규칙을 삭제하시겠습니까?</p>
        <div className="bg-gray-50 rounded-xl p-3 mb-5 text-sm text-gray-700 space-y-1">
          {rule.deviceAddress && <div><span className="font-medium text-gray-500">MAC:</span> <code className="text-xs">{rule.deviceAddress}</code></div>}
          {rule.phoneNumber && <div><span className="font-medium text-gray-500">번호:</span> {rule.phoneNumber}</div>}
          <div><span className="font-medium text-gray-500">사유:</span> {REASON_META[rule.reason]?.label ?? rule.reason}</div>
        </div>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            disabled={isPending}
            className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
          >
            취소
          </button>
          <button
            onClick={onConfirm}
            disabled={isPending}
            className="flex-1 py-2.5 bg-text-red hover:opacity-90 rounded-xl text-sm font-medium text-white disabled:opacity-50 transition-colors"
          >
            {isPending ? "삭제 중..." : "삭제"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── 메인 ──────────────────────────────────────────────────────────────────

export default function BlacklistRuleList() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // ─── URL 파라미터 ────────────────────────────────────────────────────────
  const searchTerm = searchParams.get("q") ?? "";
  const sortKey    = (searchParams.get("sort") ?? "createdAt") as SortKey;
  const sortDir    = (searchParams.get("dir")  ?? "desc") as SortDir;
  const currentPage = Math.max(1, parseInt(searchParams.get("page") ?? "1", 10));

  const updateParams = useCallback(
    (updates: Record<string, string | null | undefined>, resetPage = false) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(updates)) {
          if (v == null || v === "") next.delete(k);
          else next.set(k, v);
        }
        if (resetPage) next.delete("page");
        return next;
      }, { replace: true });
    },
    [setSearchParams]
  );

  const setSearchTerm = (v: string) => updateParams({ q: v || null }, true);
  const setCurrentPage = (p: number) => updateParams({ page: p === 1 ? null : String(p) });

  const handleSort = (key: SortKey) => {
    if (key === sortKey) updateParams({ dir: sortDir === "asc" ? "desc" : "asc" });
    else updateParams({ sort: key, dir: "desc" }, true);
  };

  // ─── 데이터 ─────────────────────────────────────────────────────────────
  const { data: rules = [], isLoading, isError } = useQuery<BlacklistRule[]>({
    queryKey: ["blacklist-rules"],
    queryFn: blacklistRuleService.getAll,
  });

  // ─── 추가 폼 상태 ────────────────────────────────────────────────────────
  const [showForm, setShowForm] = useState(false);
  const [formDeviceAddress, setFormDeviceAddress] = useState("");
  const [formPhoneNumber, setFormPhoneNumber] = useState("");
  const [formReason, setFormReason] = useState("A");
  const [formNote, setFormNote] = useState("");
  const [formDeviceVersion, setFormDeviceVersion] = useState("");
  const [formError, setFormError] = useState("");

  // ─── 모달 상태 ──────────────────────────────────────────────────────────
  const [editTarget, setEditTarget] = useState<BlacklistRule | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<BlacklistRule | null>(null);

  // ─── Mutations ───────────────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: (req: BlacklistRuleCreateRequest) => blacklistRuleService.create(req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["blacklist-rules"] });
      setShowForm(false);
      setFormDeviceAddress(""); setFormPhoneNumber("");
      setFormReason("A"); setFormNote(""); setFormDeviceVersion(""); setFormError("");
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, req }: { id: number; req: BlacklistRuleUpdateRequest }) =>
      blacklistRuleService.update(id, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["blacklist-rules"] });
      setEditTarget(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => blacklistRuleService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["blacklist-rules"] });
      setDeleteTarget(null);
    },
  });

  // ─── 통계 ───────────────────────────────────────────────────────────────
  const stats = useMemo(() => ({
    total: rules.length,
    v1: rules.filter((r) => r.deviceVersion === "V1").length,
    v2: rules.filter((r) => r.deviceVersion === "V2").length,
    none: rules.filter((r) => !r.deviceVersion).length,
  }), [rules]);

  // ─── 필터 + 정렬 ────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    let result = q
      ? rules.filter((r) =>
          r.deviceAddress?.toLowerCase().includes(q) ||
          r.phoneNumber?.includes(q) ||
          r.note?.toLowerCase().includes(q) ||
          r.deviceVersion?.toLowerCase().includes(q) ||
          r.reason.toLowerCase().includes(q) ||
          r.createdBy?.toLowerCase().includes(q)
        )
      : [...rules];

    result.sort((a, b) => {
      let av: string, bv: string;
      if (sortKey === "reason")        { av = a.reason; bv = b.reason; }
      else if (sortKey === "deviceVersion") { av = a.deviceVersion ?? ""; bv = b.deviceVersion ?? ""; }
      else                             { av = a.createdAt ?? ""; bv = b.createdAt ?? ""; }
      return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    });
    return result;
  }, [rules, searchTerm, sortKey, sortDir]);

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginated = filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const handleSubmit = () => {
    const mac = formDeviceAddress.trim();
    const phone = formPhoneNumber.trim();
    if (!mac && !phone) { setFormError("기기 주소 또는 전화번호 중 하나 이상 입력해야 합니다."); return; }
    setFormError("");
    createMutation.mutate({
      deviceAddress: mac || undefined,
      phoneNumber: phone || undefined,
      reason: formReason,
      note: formNote.trim() || undefined,
      deviceVersion: formDeviceVersion || undefined,
    });
  };

  if (isLoading) return <LoadingSpinner />;
  if (isError) return <ErrorAlert />;

  return (
    <div>
      {/* ── 헤더 ─────────────────────────────────────────────────────── */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800">블랙리스트 관리</h1>
        <p className="text-gray-500 mt-2">MAC 주소 또는 전화번호를 사전 등록하여 등록 시도를 차단합니다</p>
      </div>

      {/* ── 통계 카드 ─────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-800">
            <i className="fas fa-ban mr-2 text-primary"></i>
            등록 현황
          </h2>
          <button
            onClick={() => setShowForm((v) => !v)}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium transition-all shadow-md ${
              showForm
                ? "bg-gray-100 text-gray-600 hover:bg-gray-200"
                : "bg-gradient-to-r from-text-red to-[#e05555] text-white hover:opacity-90"
            }`}
          >
            <i className={`fas ${showForm ? "fa-times" : "fa-plus"}`}></i>
            {showForm ? "닫기" : "규칙 추가"}
          </button>
        </div>
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: "전체",   count: stats.total, cls: "bg-gradient-to-r from-primary to-[#5FA060]" },
            { label: "V1",     count: stats.v1,    cls: "bg-gradient-to-r from-blue-500 to-blue-600" },
            { label: "V2",     count: stats.v2,    cls: "bg-gradient-to-r from-purple-500 to-purple-600" },
            { label: "미지정", count: stats.none,  cls: "bg-gradient-to-r from-gray-400 to-gray-500" },
          ].map(({ label, count, cls }) => (
            <div key={label} className={`${cls} text-white rounded-lg p-4 text-center shadow-md`}>
              <div className="text-xs opacity-80">{label}</div>
              <div className="text-2xl font-bold mt-1">{count}</div>
              <div className="text-xs opacity-70 mt-1">건</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── 추가 폼 ──────────────────────────────────────────────────── */}
      {showForm && (
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border-l-4 border-text-red">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <i className="fas fa-ban text-text-red"></i>
            새 블랙리스트 규칙 등록
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                기기 주소 (MAC) <span className="text-gray-400 font-normal text-xs">선택</span>
              </label>
              <input
                type="text"
                value={formDeviceAddress}
                onChange={(e) => setFormDeviceAddress(e.target.value)}
                placeholder="예: C6:52:27:DA:23:3E"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-mono"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                전화번호 <span className="text-gray-400 font-normal text-xs">선택</span>
              </label>
              <input
                type="text"
                value={formPhoneNumber}
                onChange={(e) => setFormPhoneNumber(e.target.value)}
                placeholder="예: 01012345678"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">사유 코드</label>
              <select
                value={formReason}
                onChange={(e) => setFormReason(e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                {REASONS.map((r) => (
                  <option key={r} value={r}>{REASON_META[r]?.label ?? `사유 ${r}`}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                기기 버전 <span className="text-gray-400 font-normal text-xs">선택</span>
              </label>
              <select
                value={formDeviceVersion}
                onChange={(e) => setFormDeviceVersion(e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                {DEVICE_VERSION_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                메모 <span className="text-gray-400 font-normal text-xs">선택</span>
              </label>
              <input
                type="text"
                value={formNote}
                onChange={(e) => setFormNote(e.target.value)}
                placeholder="관리자 메모 (예: 분실 신고)"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>
          {formError && (
            <p className="text-text-red text-sm mb-3">
              <i className="fas fa-exclamation-circle mr-1"></i>{formError}
            </p>
          )}
          <div className="flex gap-3">
            <button
              onClick={handleSubmit}
              disabled={createMutation.isPending}
              className="px-6 py-2.5 bg-text-red text-white rounded-xl font-medium hover:opacity-90 transition-all disabled:opacity-50 shadow-md"
            >
              {createMutation.isPending ? "등록 중..." : "등록"}
            </button>
            <button
              onClick={() => { setShowForm(false); setFormError(""); }}
              className="px-6 py-2.5 border border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-all"
            >
              취소
            </button>
          </div>
        </div>
      )}

      {/* ── 검색 + 필터 ──────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <div className="relative">
          <i className="fas fa-search absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
          <input
            type="text"
            placeholder="MAC, 전화번호, 버전, 사유, 메모, 등록자 검색..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
        <div className="mt-4 text-sm text-gray-500">
          총 <span className="font-semibold text-primary">{filtered.length}</span>건의 규칙
        </div>
      </div>

      {/* ── 테이블 ───────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-150">
            <thead className="bg-gradient-to-r from-primary-lighter to-bg-light">
              <tr>
                {([
                  { label: "번호",         sortable: false, key: null,            minW: "min-w-[70px]" },
                  { label: "기기 주소 (MAC)", sortable: false, key: null,          minW: "min-w-[200px]" },
                  { label: "전화번호",      sortable: false, key: null,            minW: "min-w-[140px]" },
                  { label: "버전",          sortable: true,  key: "deviceVersion", minW: "min-w-[100px]" },
                  { label: "사유",          sortable: true,  key: "reason",        minW: "min-w-[130px]" },
                  { label: "메모",          sortable: false, key: null,            minW: "min-w-[180px]" },
                  { label: "등록자",        sortable: false, key: null,            minW: "min-w-[110px]" },
                  { label: "등록일",        sortable: true,  key: "createdAt",     minW: "min-w-[150px]" },
                  { label: "관리",          sortable: false, key: null,            minW: "min-w-[90px]"  },
                ] as const).map(({ label, sortable, key, minW }) => (
                  <th
                    key={label}
                    className={`px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider ${minW} ${sortable ? "cursor-pointer hover:bg-primary-lighter/50 transition-colors" : ""}`}
                    onClick={sortable && key ? () => handleSort(key as SortKey) : undefined}
                  >
                    <div className="flex items-center justify-center space-x-1 whitespace-nowrap">
                      <span>{label}</span>
                      {sortable && key && <SortIcon col={key as SortKey} sortKey={sortKey} sortDir={sortDir} />}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-150">
              {paginated.length > 0 ? (
                paginated.map((rule, idx) => (
                  <tr key={rule.id} className="hover:bg-primary-lighter transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className="text-primary font-semibold">#{(currentPage - 1) * ITEMS_PER_PAGE + idx + 1}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {rule.deviceAddress
                        ? <code className="text-xs bg-gray-50 px-2 py-1 rounded text-gray-600">{rule.deviceAddress}</code>
                        : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                      {rule.phoneNumber
                        ? <><i className="fas fa-phone text-gray-400 mr-2"></i>{rule.phoneNumber}</>
                        : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <VersionBadge version={rule.deviceVersion} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <ReasonBadge reason={rule.reason} />
                    </td>
                    <td className="px-6 py-4 text-center text-sm text-gray-600 max-w-xs truncate" title={rule.note ?? ""}>
                      {rule.note ? (rule.note.length > 25 ? `${rule.note.substring(0, 25)}...` : rule.note) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500">
                      {rule.createdBy ?? <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500">
                      <i className="fas fa-calendar-alt mr-2"></i>{formatDate(rule.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => setEditTarget(rule)}
                          className="px-3 py-1 inline-flex items-center text-xs leading-5 font-semibold rounded-full transition-all hover:opacity-80 hover:scale-105 bg-blue-50 text-blue-600 border border-blue-200"
                          title="수정"
                        >
                          <i className="fas fa-pen mr-1"></i>수정
                        </button>
                        <button
                          onClick={() => setDeleteTarget(rule)}
                          className="px-3 py-1 inline-flex items-center text-xs leading-5 font-semibold rounded-full transition-all hover:opacity-80 hover:scale-105 bg-text-red/10 text-text-red border border-text-red/30"
                          title="삭제"
                        >
                          <i className="fas fa-ban mr-1"></i>삭제
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={9} className="px-6 py-12 text-center text-gray-500">
                    <i className="fas fa-search text-4xl mb-4 text-gray-300 block"></i>
                    <p>{searchTerm ? "검색 결과가 없습니다" : "등록된 블랙리스트 규칙이 없습니다"}</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* 페이지네이션 */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-150 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                <span className="font-medium">{filtered.length}</span>개 중{" "}
                <span className="font-medium">{(currentPage - 1) * ITEMS_PER_PAGE + 1}</span>-
                <span className="font-medium">{Math.min(currentPage * ITEMS_PER_PAGE, filtered.length)}</span>개 표시
              </div>
              <div className="flex items-center space-x-2">
                {([
                  { label: <i className="fas fa-angle-double-left"></i>, page: 1,               disabled: currentPage === 1 },
                  { label: <i className="fas fa-angle-left"></i>,        page: currentPage - 1, disabled: currentPage === 1 },
                ] as const).map(({ label, page, disabled }, i) => (
                  <button key={i} onClick={() => setCurrentPage(page)} disabled={disabled}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${disabled ? "bg-gray-200 text-gray-400 cursor-not-allowed" : "bg-white text-gray-700 hover:bg-primary hover:text-white shadow-sm"}`}>
                    {label}
                  </button>
                ))}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let p: number;
                  if (totalPages <= 5)                 p = i + 1;
                  else if (currentPage <= 3)           p = i + 1;
                  else if (currentPage >= totalPages - 2) p = totalPages - 4 + i;
                  else                                 p = currentPage - 2 + i;
                  return (
                    <button key={p} onClick={() => setCurrentPage(p)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${currentPage === p ? "bg-gradient-to-r from-primary to-[#5FA060] text-white shadow-md" : "bg-white text-gray-700 hover:bg-gray-100 shadow-sm"}`}>
                      {p}
                    </button>
                  );
                })}
                {([
                  { label: <i className="fas fa-angle-right"></i>,        page: currentPage + 1, disabled: currentPage === totalPages },
                  { label: <i className="fas fa-angle-double-right"></i>, page: totalPages,      disabled: currentPage === totalPages },
                ] as const).map(({ label, page, disabled }, i) => (
                  <button key={i} onClick={() => setCurrentPage(page)} disabled={disabled}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${disabled ? "bg-gray-200 text-gray-400 cursor-not-allowed" : "bg-white text-gray-700 hover:bg-primary hover:text-white shadow-sm"}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── 모달 ─────────────────────────────────────────────────────── */}
      {editTarget && (
        <EditModal
          rule={editTarget}
          isPending={updateMutation.isPending}
          onConfirm={(req) => updateMutation.mutate({ id: editTarget.id, req })}
          onCancel={() => setEditTarget(null)}
        />
      )}
      {deleteTarget && (
        <ConfirmDeleteModal
          rule={deleteTarget}
          isPending={deleteMutation.isPending}
          onConfirm={() => deleteMutation.mutate(deleteTarget.id)}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
